import { LoadingButton } from "@mui/lab";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	TextField,
} from "@mui/material";
import moment from "moment";
import { useEffect } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CommCard from "src/common/CommCard";
import { removeUndefinedAndNull } from "src/common/removeUndefinedAndNull";
import SelectAccountListByMethod from "src/common/SelectAccountListByMethod";
import VisitorSelectFieldList from "src/common/VisitorSelectFieldList";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import type { IPayableItem } from "src/Types/module/paymentableItem/itemList";
import MultipleInvoiceItems from "../../exhibitor/MultipleInvoiceItems";

type IProps = {
	handleClose?: any;
};
const CreateVisitorInvoice = ({ handleClose }: IProps) => {
	const { register, handleSubmit, control, watch, reset, setValue } =
		useForm();
	const { fields, append, remove } = useFieldArray({
		control,
		name: "items",
	});
	const discount = useWatch({
		control,
		name: `discount`,
	});
	const vat = useWatch({
		control,
		name: `vat`,
	});
	const { data: payableItems = [] } = useApiQuery<IPayableItem[]>({
		endPoint: `/api/admin/payment-items`,
		queryKeys: ["PayableItems"],
		params: {},
	});
	const { mutate, isPending, isSuccess, data } = useApiMutation<
		void,
		{ id: number }
	>({
		endPoint: "/api/admin/visitor/invoice",
		method: "POST",
		invalidateQueries: [""],
	});

	const withMoneyReceipt = watch("with_money_receipt");
	const navigate = useNavigate();
	const paymentMethod = watch("payment_method");
	const items = watch("items");

	if (fields.length === 0) {
		append({ id: "", amount: "" });
	}
	const totalItemAmount = items?.reduce(
		(sum, item) =>
			sum + Number(item.amount || 0) * (Number(item.quantity) || 1),
		0
	);
	const netTotal = totalItemAmount - Number(discount || 0) + Number(vat || 0);

	const onSubmit = async (submitData: any) => {
		submitData.sub_total = totalItemAmount;
		submitData.net_total = netTotal;
		mutate(removeUndefinedAndNull(submitData));
	};
	useEffect(() => {
		if (isSuccess && data) {
			reset();
			navigate(`/visitor/visitor-invoice/${data?.data?.id}`);
		}
	}, [isSuccess, data]);
	return (
		<CommCard
			helmetTitle='BTTF - Create a exhibitor invoice'
			headerTitle='Create Visitor Invoice'
			showCardHeader
		>
			<Box
				sx={{
					maxWidth: "1200px",
					margin: "20px auto",
					px: "20px",
				}}
				component='form'
				onSubmit={handleSubmit(onSubmit)}
			>
				<Grid container spacing={1}>
					<Grid item xs={12} md={12} lg={6}>
						<VisitorSelectFieldList
							mt='0'
							fieldName='visitor_id'
							register={register}
							isRequired
							hideCustomLabel
						/>
					</Grid>
					<Grid item xs={12} md={12} lg={6}>
						<TextField
							fullWidth
							required
							id='invoice_date'
							label='Invoice Date'
							type='date'
							defaultValue={moment().format("YYYY-MM-DD")}
							InputLabelProps={{ shrink: true }}
							variant='outlined'
							{...register("invoice_date")}
						/>
					</Grid>
					<Grid item xs={12}>
						{fields?.map((item, index) => {
							return (
								<MultipleInvoiceItems
									register={register}
									allPaymentableItem={payableItems}
									item={item}
									index={index}
									key={index}
									control={control}
									remove={remove}
									fields={fields}
									setValue={setValue}
								/>
							);
						})}
					</Grid>
					<Grid
						item
						xs={12}
						sx={{ textAlign: "end", paddingTop: "0px !important" }}
					>
						<Button
							type='button'
							variant='outlined'
							onClick={() => append({ id: "", amount: "" })}
						>
							Add Item
						</Button>
					</Grid>

					<Grid item xs={12}>
						<Grid
							container
							spacing={1}
							sx={{
								background: "#fbfbfb",
								borderRadius: "15px",
								p: 1,
							}}
						>
							{/* Discount Field */}
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									id='discount'
									label='Discount'
									type='text'
									InputLabelProps={{ shrink: true }}
									variant='outlined'
									{...register("discount")}
								/>
							</Grid>

							{/* VAT Field */}
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									id='vat'
									label='VAT'
									type='text'
									InputLabelProps={{ shrink: true }}
									variant='outlined'
									{...register("vat")}
								/>
							</Grid>
						</Grid>
					</Grid>

					<Grid container>
						<Grid item xs={12}>
							<FormControlLabel
								style={{ marginLeft: "6px" }}
								control={
									<Checkbox
										{...register("with_money_receipt")}
									/>
								}
								label='With Money Receipt'
							/>
						</Grid>
					</Grid>

					{withMoneyReceipt && (
						<SelectAccountListByMethod
							register={register}
							md={12}
							lg={6}
							watch={watch}
							control={control}
						/>
					)}

					{paymentMethod === "Bank" && withMoneyReceipt && (
						<>
							<Grid item xs={12} md={12} lg={6}>
								<TextField
									fullWidth
									required
									id='BankName'
									label='Bank Name'
									type='text'
									variant='outlined'
									{...register("BankName")}
								/>
							</Grid>
							<Grid item xs={12} md={12} lg={6}>
								<TextField
									fullWidth
									id='BranchName'
									label='Branch Name'
									type='text'
									variant='outlined'
									{...register("BranchName")}
								/>
							</Grid>
							<Grid item xs={12} md={12} lg={6}>
								<TextField
									fullWidth
									id='ChequeNo'
									label='Cheque Number'
									type='text'
									variant='outlined'
									{...register("ChequeNo")}
								/>
							</Grid>

							<Grid item xs={12} md={12} lg={6}>
								<TextField
									fullWidth
									id='ChequeDate'
									label='Cheque Date'
									type='date'
									// defaultValue={moment().format('YYYY-MM-DD')}
									InputLabelProps={{ shrink: true }}
									variant='outlined'
									{...register("ChequeDate")}
								/>
							</Grid>
						</>
					)}
				</Grid>

				<Grid sx={{ mt: 2 }} item xs={12} md={12} lg={12}>
					<TextField
						fullWidth
						rows={3}
						multiline
						id='remark'
						label='Remark'
						type='text'
						variant='outlined'
						{...register("remark")}
					/>
				</Grid>
				<p style={{ fontSize: "14px", fontWeight: "bold" }}>
					Total Invoice Amount/Subtotal : {totalItemAmount}
				</p>
				<p style={{ fontSize: "14px", fontWeight: "bold" }}>
					Net Total : {netTotal || 0}
				</p>
				<Box textAlign={"center"} mt={3}>
					{isPending ? (
						<LoadingButton
							loading={isPending}
							fullWidth
							color='secondary'
							disableElevation
							sx={{
								mt: 3,
								mb: 2,
								py: 1,
								width: "100%",
							}}
							variant='contained'
						>
							Loading...
						</LoadingButton>
					) : (
						<Button
							type='submit'
							variant='contained'
							disableElevation
							sx={{
								mt: 3,
								mb: 2,
								py: 1,
								width: "100%",
							}}
						>
							Create Invoice
						</Button>
					)}
				</Box>
			</Box>
		</CommCard>
	);
};

export default CreateVisitorInvoice;
